import { AvatarLive } from './avatar-live';
import './demo.css';
import type { AvatarState } from './types';

const stage = query<HTMLElement>('#avatar-stage');
const cover = query<HTMLElement>('#cover');
const dock = query<HTMLElement>('#dock');
const conversation = query<HTMLElement>('#conversation');
const messages = query<HTMLOListElement>('#messages');
const caption = query<HTMLParagraphElement>('#caption');
const status = query<HTMLElement>('#status');
const startError = query<HTMLParagraphElement>('#start-error');
const start = query<HTMLButtonElement>('#start');
const stop = query<HTMLButtonElement>('#stop');
const mic = query<HTMLButtonElement>('#mic');
const send = query<HTMLButtonElement>('#send');
const interruptions = query<HTMLButtonElement>('#interruptions');
const text = query<HTMLInputElement>('#text');
const showConversation = query<HTMLButtonElement>('#show-conversation');
const hideConversation = query<HTMLButtonElement>('#hide-conversation');
const resumeSound = query<HTMLButtonElement>('#resume-sound');

const STATE_LABELS: Record<AvatarState, { text: string; tone: string }> = {
  disconnected: { text: '未连接', tone: 'idle' },
  connecting: { text: '正在接入…', tone: 'busy' },
  idle: { text: '已就绪', tone: 'live' },
  listening: { text: '正在聆听', tone: 'live' },
  thinking: { text: '正在思考', tone: 'busy' },
  speaking: { text: '正在回答', tone: 'live' },
};

// Session creation is authenticated. A demo deployment passes its own key via ?key=.
const apiKey = new URLSearchParams(location.search).get('key') ?? 'dev-avatar-api-key';
// Per-visitor id so one visitor cannot exhaust the daily quota for everyone else.
const userId = visitorId();

let avatar: AvatarLive | undefined;
let micOn = false;
let interruptionsEnabled = true;
let interruptionChangePending = false;
let pendingReply = '';

start.addEventListener('click', async () => {
  setStartError('');
  start.disabled = true;
  start.textContent = '正在接入…';
  avatar = new AvatarLive({
    sessionEndpoint: '/v1/sessions',
    sessionHeaders: { Authorization: `Bearer ${apiKey}` },
    container: stage,
    renderMode: 'auto',
    metadata: { user_id: userId },
  });
  bindEvents(avatar);

  try {
    await avatar.connect();
  } catch (error) {
    await avatar.disconnect().catch(() => undefined);
    avatar = undefined;
    const message = describe(error);
    setStatus(message, 'error');
    setStartError(message);
    start.disabled = false;
    start.textContent = '开始对话';
    return;
  }

  cover.hidden = true;
  dock.hidden = false;
  interruptionsEnabled = true;
  syncInterruptionButton();
  // Focusing on a phone would pop the keyboard over the avatar before the user asked for it.
  if (matchMedia('(pointer: fine)').matches) text.focus();
  await enableMic();
});

stop.addEventListener('click', async () => {
  const session = avatar;
  avatar = undefined;
  micOn = false;
  syncMicButton();
  interruptionsEnabled = true;
  interruptionChangePending = false;
  syncInterruptionButton();
  dock.hidden = true;
  conversation.hidden = true;
  showConversation.setAttribute('aria-expanded', 'false');
  caption.hidden = true;
  resumeSound.hidden = true;
  cover.hidden = false;
  start.disabled = false;
  start.textContent = '开始对话';
  setStartError('');
  messages.replaceChildren();
  try {
    await session?.disconnect();
  } catch (error) {
    setStatus(describe(error), 'error');
  }
});

mic.addEventListener('click', async () => {
  if (!avatar) return;
  if (micOn) {
    avatar.stopMic();
    micOn = false;
    syncMicButton();
    return;
  }
  await enableMic();
});

interruptions.addEventListener('click', async () => {
  if (!avatar || interruptionChangePending) return;
  const next = !interruptionsEnabled;
  interruptionChangePending = true;
  syncInterruptionButton();
  try {
    await avatar.setInterruptionsEnabled(next);
    interruptionsEnabled = next;
  } catch (error) {
    setStatus(describe(error), 'error');
  } finally {
    interruptionChangePending = false;
    syncInterruptionButton();
  }
});

send.addEventListener('click', () => void submitText());
text.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') void submitText();
});

showConversation.addEventListener('click', () => setConversationOpen(conversation.hidden));
hideConversation.addEventListener('click', () => setConversationOpen(false));

resumeSound.addEventListener('click', async () => {
  try {
    await avatar?.resumeAudio();
    resumeSound.hidden = true;
  } catch (error) {
    setStatus(describe(error), 'error');
  }
});

trackKeyboardInset();

function trackKeyboardInset(): void {
  const viewport = window.visualViewport;
  if (!viewport) return;
  const sync = (): void => {
    const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    document.documentElement.style.setProperty('--keyboard', `${Math.round(inset)}px`);
  };
  viewport.addEventListener('resize', sync);
  viewport.addEventListener('scroll', sync);
  sync();
}

function bindEvents(session: AvatarLive): void {
  session.on('stateChange', (state) => {
    const label = STATE_LABELS[state];
    setStatus(label.text, label.tone);
    const live = state !== 'disconnected' && state !== 'connecting';
    stop.disabled = !live;
    send.disabled = !live;
    mic.disabled = !live;
    if (!interruptionChangePending) interruptions.disabled = !live;
    // Greeting-style utterances stream deltas but never send a final, so commit
    // whatever is pending once the turn ends instead of leaking it into the next reply.
    if (state !== 'speaking') flushReply();
    if (state === 'listening') clearCaption();
  });

  session.on('userTranscript', ({ text: value, final }) => {
    showCaption(value);
    if (final && value.trim()) addMessage('user', value);
  });

  session.on('avatarTranscript', ({ text: value, final }) => {
    pendingReply = final ? value : pendingReply + value;
    showCaption(pendingReply);
    if (final) flushReply();
  });

  session.on('interrupted', () => {
    flushReply();
    clearCaption();
  });

  session.on('error', ({ code, message }) => {
    setStatus(code === 'AUTOPLAY_BLOCKED' ? '浏览器已静音，请开启声音' : message, 'error');
    if (code === 'AUTOPLAY_BLOCKED') resumeSound.hidden = false;
  });
}

async function enableMic(): Promise<void> {
  if (!avatar) return;
  try {
    await avatar.startMic();
    micOn = true;
  } catch {
    micOn = false;
    addMessage('note', '麦克风不可用，可继续使用文字对话');
  }
  syncMicButton();
}

async function submitText(): Promise<void> {
  const value = text.value.trim();
  if (!value || !avatar) return;
  text.value = '';
  addMessage('user', value);
  showCaption(value);
  try {
    await avatar.say(value);
  } catch (error) {
    text.value = value;
    setStatus(describe(error), 'error');
  }
}

function flushReply(): void {
  const value = pendingReply;
  pendingReply = '';
  if (value.trim()) addMessage('avatar', value);
}

function setConversationOpen(open: boolean): void {
  conversation.hidden = !open;
  showConversation.setAttribute('aria-expanded', String(open));
  if (open) messages.scrollTop = messages.scrollHeight;
}

function addMessage(kind: 'user' | 'avatar' | 'note', value: string): void {
  const item = document.createElement('li');
  item.className = kind;
  item.textContent = value;
  messages.append(item);
  messages.scrollTop = messages.scrollHeight;
}

function showCaption(value: string): void {
  caption.textContent = value;
  caption.hidden = !value.trim();
}

function clearCaption(): void {
  caption.textContent = '';
  caption.hidden = true;
}

function syncMicButton(): void {
  mic.setAttribute('aria-pressed', String(micOn));
  mic.textContent = micOn ? '麦克风已开' : '开启麦克风';
}

function syncInterruptionButton(): void {
  interruptions.disabled = !avatar || interruptionChangePending || avatar.state === 'disconnected';
  interruptions.setAttribute('aria-pressed', String(interruptionsEnabled));
  interruptions.textContent = interruptionChangePending
    ? '设置中…'
    : interruptionsEnabled
      ? '允许打断'
      : '不打断';
  interruptions.title = interruptionsEnabled
    ? '检测到你的语音时停止回答'
    : '回答过程中不会因麦克风语音停止';
}

function setStatus(message: string, tone: string): void {
  status.textContent = message;
  status.dataset.tone = tone;
}

function setStartError(message: string): void {
  startError.textContent = message;
  startError.hidden = !message.trim();
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function visitorId(): string {
  const stored = localStorage.getItem('avatar-demo-visitor');
  if (stored) return stored;
  const created = `demo-${crypto.randomUUID()}`;
  localStorage.setItem('avatar-demo-visitor', created);
  return created;
}

function query<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`demo page is missing ${selector}`);
  return element;
}
